import { Component, inject, OnInit, AfterViewInit, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  Alert,
  AlertService,
  AlertType,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig,
  TimepickerComponent
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { catchError, concatMap, finalize, map, Observable, of, timeout } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SharedDialogComponent } from 'src/app/shared/components/shared-dialog/shared-dialog.component';
import { DealerState, UpdatePlantsAction, UpdateSitesAction } from 'src/app/shared/state/DealerState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { BackOfficeAuditTrail } from '../../models/backOfficeAuditTrail.model';
import { BackofficeActionOption, BackOfficeAuditFilter, buildBackofficeActionOptions } from '../../models/bo-audit-filter.model';
import { BoAuditExportResponse, BoAuditService } from '../../services/bo-audit.service';
import { PaginatedBoAuditResponse } from '../../models/paginated-bo-audit.model';
import { PlantService } from '../../../../shared/services/plant.service';
import { SiteService } from '../../../../shared/services/site.service';
import { AuditTrail } from '../../models/auditTrail.model';
import { ActionState, AuditFilter } from '../../models/audit-filter.model';
import { AuditExportResponse, AuditService } from '../../services/audit.service';
import { PaginatedAuditResponse } from '../../models/paginated-audit.model';
import { EGRA_POSTS } from '../../../setup-screens/models/plant-posts-model';
import { MatSelectChange } from '@angular/material/select';
import { HubLog } from '../../models/hubLog.model';
import { PaginatedHubLogResponse } from '../../models/paginated-hub-log.model';
import { HubLogFilter } from '../../models/hubLog-filter.model';
import { HubLogService } from '../../services/hub-log.service';
import { HubEventLog } from '../../models/hubEventLog.model';
import { PaginatedHubEventLogResponse } from '../../models/paginated-hub-event-log.model';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { TransactionLog } from '../../models/transactionLog.model';
import { TransactionLogFilter, TransactionResultList } from '../../models/transaction-log-filter.model';
import { TransactionLogService } from '../../services/transaction-log.service';
import { PaginatedTransactionLogResponse } from '../../models/paginated-transaction-log.model';
import { convertUtcToLocalDisplay } from '../../../../shared/helper/date-time-helper';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';

@Component({
  selector: 'app-logs',
  templateUrl: './logs.component.html',
  styleUrls: ['./logs.component.scss'],
  standalone: false
})
export class LogsComponent implements OnInit, AfterViewInit {
  backOfficeAuditTrailForm: FormGroup;
  auditTrailForm: FormGroup;
  hubLogForm: FormGroup;
  transactionLogForm: FormGroup;

  public store = inject(Store);
  public router = inject(Router);
  public fb = inject(FormBuilder);
  public boAuditService = inject(BoAuditService);
  public hubLogService = inject(HubLogService);
  public auditService = inject(AuditService);
  public alertService = inject(AlertService);
  public translationService = inject(TranslationService);
  public plantService = inject(PlantService);
  public siteService = inject(SiteService);
  public featureflagsService = inject(FeatureflagsService);
  public transactionLogService = inject(TransactionLogService);
  public dialog = inject(MatDialog);

  locations: string[] = [];
  plantNames: string[] = [];
  siteNames: string[] = [];
  actionList: BackofficeActionOption[] = [];
  auditActionList: string[] = [];
  userLoginNames: string[] = [];
  dealer: Dealer;
  userLogin: string;
  pageSizeOptions = [10, 25, 50, 100];
  sites = this.store.selectSnapshot(DealerState.sites);
  boAuditTableActive: boolean = false;
  hubLogTableActive: boolean = false;
  showHubLogDetails: boolean = false;
  isHubLogEnabled: boolean = false;
  isDeleteHubRecordLoading = false;
  isAuditTrailSearchLoading = false;
  isBoAuditSearchLoading = false;
  isTransactionLogSearchLoading = false;
  isHubLogSearchLoading = false;
  isAuditTrailExportLoading = false;
  isAuditExportLimitLoading = false;
  isBoAuditExportLoading = false;
  isAuditTrailSearchCompleted = false;
  isBoAuditSearchCompleted = false;
  // Generic release gate (see FEATURE_FLAGS.EARLY_ACCESS_ENABLED) — currently used by audit export.
  isEarlyAccessEnabled = false;
  selectedHubLogForDetails: HubLog | null = null;
  egraPostNames: string[] = [];
  isCustomDateRange = false;
  hubStatus: string[] = ['Success', 'Fail'];
  hubProcess: string[] = [];
  isHubProcessLoading = false;
  isHubProcessLoaded = false;

  transactionLogTableActive: boolean = false;
  transactionTypeList: string[] = [];
  transactionResultList: string[] = TransactionResultList;
  maxTransactionDate: Date = new Date();
  isTransactionTypeLoading = false;
  isTransactionTypeLoaded = false;

  maxDate: Date = new Date(); // Today's date
  minDate: Date = new Date(new Date().setFullYear(new Date().getFullYear() - 3));
  dateFilterOptions: { value: string; label: string }[] = [];
  private readonly auditExportModalId = 'audit-trail-export-modal';
  private readonly boAuditExportModalId = 'bo-audit-trail-export-modal';
  private readonly defaultAuditExportMaxRows = 10000;
  private readonly boAuditExportMaxRows = 10000;
  private readonly auditExportChunkSize = 25000;
  private auditExportMaxRows = this.defaultAuditExportMaxRows;

  @ViewChild('boAuditTable', { static: false }) tableRef: TableComponent<BackOfficeAuditTrail>;
  @ViewChild('auditTable', { static: false }) tableRef2: TableComponent<AuditTrail>;
  @ViewChild('hubLogTable', { static: false }) tableRef3: TableComponent<HubLog>;
  @ViewChild('hubEventLogTable', { static: false }) tableRefHubEvent: TableComponent<HubEventLog>;
  @ViewChild('transactionLogTable', { static: false }) tableRefTransactionLog: TableComponent<TransactionLog>;

  @ViewChildren(TimepickerComponent) timepickers: QueryList<TimepickerComponent>;

  /**
   * Feature flag to enable/disable Back Office Audit functionality
   */
  tableConfig: TableConfig<BackOfficeAuditTrail> = {
    columns: {
      userId: {
        cell: (item: BackOfficeAuditTrail) => item.userId,
        header: this.translationService.translate('userIdText')
      },
      record: {
        cell: (item: BackOfficeAuditTrail) => item.keyData,
        header: this.translationService.translate('recordText')
      },
      btm: {
        cell: (item: BackOfficeAuditTrail) => item.btm,
        header: this.translationService.translate('btmWorkOrderText')
      },
      date: {
        cell: (item: BackOfficeAuditTrail) => item.createDt,
        header: this.translationService.translate('dateText'),
        sortable: true
      },
      table: {
        cell: (item: BackOfficeAuditTrail) => item.table,
        header: this.translationService.translate('tableText')
      },
      action: {
        cell: (item: BackOfficeAuditTrail) => this.translationService.translate(item.action),
        header: this.translationService.translate('actionText')
      },
      details: {
        // Convert literal "\n" sequences to real line breaks; rendered as separate lines via CSS (white-space: pre-line)
        cell: (item: BackOfficeAuditTrail) => (item.details ?? '').replace(/\\n/g, '\n'),
        header: this.translationService.translate('detailsText')
      }
    },
    paginator: {
      pageSize: 10,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: 'Items per page'
      }
    }
  };

  /**
   * Feature flag to enable/disable Back Office Audit functionality
   */
  tableConfigHub: TableConfig<HubLog> = {
    columns: {
      nativeId: {
        cell: (item: HubLog) => item.nativeId,
        header: this.translationService.translate('nativeIdText')
      },
      process: {
        cell: (item: HubLog) => item.nativeId?.split('_')[0] ?? '',
        header: this.translationService.translate('processText')
      },
      startDate: {
        cell: (item: HubLog) => convertUtcToLocalDisplay(item.startDate),
        header: this.translationService.translate('startDateText'),
        sortable: true
      },
      endDate: {
        cell: (item: HubLog) => convertUtcToLocalDisplay(item.endDate),
        header: this.translationService.translate('endDateText')
      },
      endStatus: {
        cell: 'endStatusCell',
        header: this.translationService.translate('endStatusText')
      }
    },
    paginator: {
      pageSize: 10,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: 'Items per page'
      }
    }
  };

  tableConfigHubDetail: TableConfig<HubEventLog> = {
    columns: {
      nativeId: {
        cell: (item: HubEventLog) => item.nativeId,
        header: this.translationService.translate('nativeIdText')
      },
      sourceEndpointId: {
        cell: (item: HubEventLog) => item.sourceEndpointId,
        header: this.translationService.translate('sourceEndpointIdText')
      },
      targetEndpointId: {
        cell: (item: HubEventLog) => item.targetEndpointId,
        header: this.translationService.translate('targetEndpointIdText')
      },
      sequenceId: {
        cell: (item: HubEventLog) => item.sequenceId,
        header: this.translationService.translate('sequenceIdText')
      },
      businessId: {
        cell: (item: HubEventLog) => item.businessId,
        header: this.translationService.translate('businessIdText')
      },
      techId: {
        cell: (item: HubEventLog) => item.techId,
        header: this.translationService.translate('techIdText')
      },
      creationDate: {
        cell: (item: HubEventLog) => convertUtcToLocalDisplay(item.creationDate),
        header: this.translationService.translate('creationDateText')
      },
      timeZoneCol: {
        cell: (item: HubEventLog) => item.timeZone,
        header: this.translationService.translate('timeZoneText')
      },
      eventStatus: {
        cell: (item: HubEventLog) => item.eventStatus,
        header: this.translationService.translate('eventStatusText')
      },
      message: {
        cell: (item: HubEventLog) => item.message,
        header: this.translationService.translate('messageText')
      },
      componentName: {
        cell: (item: HubEventLog) => item.componentName,
        header: this.translationService.translate('componentNameText')
      },
      subComponentName: {
        cell: (item: HubEventLog) => item.subComponentName,
        header: this.translationService.translate('subComponentNameText')
      }
    },
    paginator: {
      pageSize: 10,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: 'Items per page'
      }
    }
  };

  tableConfigTransactionLog: TableConfig<TransactionLog> = {
    columns: {
      userId: {
        cell: (item: TransactionLog) => item.userId,
        header: this.translationService.translate('userIdText')
      },
      transactionType: {
        cell: (item: TransactionLog) => item.transactionType,
        header: this.translationService.translate('transactionTypeText')
      },
      transactionDate: {
        cell: (item: TransactionLog) => item.transactionDate,
        header: this.translationService.translate('transactionDateText'),
        sortable: true
      },
      transactionResult: {
        cell: (item: TransactionLog) => item.transactionResult,
        header: this.translationService.translate('transactionResultText')
      },
      info: {
        cell: (item: TransactionLog) => item.info,
        header: this.translationService.translate('infoText')
      },
      transactionId: {
        cell: (item: TransactionLog) => item.transactionId,
        header: this.translationService.translate('transactionIdText')
      }
    },
    paginator: {
      pageSize: 10,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: 'Items per page'
      }
    }
  };

  constructor() {
    this.backOfficeAuditTrailForm = this.fb.group({
      userId: [''],
      barcode: ['', [Validators.maxLength(10)]],
      workOrderNumber: ['', [Validators.maxLength(10)]],
      record: [''],
      btm: [''],
      date: [''],
      table: [''],
      action: [''],
      details: [''],
      createDateStart: [''],
      createDateEnd: ['']
    });
    this.auditTrailForm = this.fb.group({
      dateFilter: ['', Validators.required],
      userId: [''],
      barcode: [''],
      location: [''],
      customDateRange: this.fb.group({
        creationDateStart: [''],
        creationDateEnd: ['']
      }),
      postName: [''],
      auditAction: [''],
      casingInfoDetail: ['']
    });
    this.hubLogForm = this.fb.group({
      date: [''],
      process: [''],
      status: [''],
      timeFrom: [''],
      timeTo: ['']
    });
    this.transactionLogForm = this.fb.group({
      userId: [''],
      transactionType: [''],
      transactionDate: [''],
      transactionResult: ['']
    });
  }

  // Initialize component
  ngAfterViewInit(): void {
    // Override decrementHour on timepickers to allow decrementing down to 00
    this.patchTimepickerDecrement();
    // Auto-set period to AM when user uses arrow keys
    this.patchTimepickerDefaultPeriod();
    // Also patch when timepickers appear later (e.g. after tab switch)
    this.timepickers.changes.subscribe(() => {
      this.patchTimepickerDecrement();
      this.patchTimepickerDefaultPeriod();
    });
  }

  ngOnInit(): void {
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.userLogin = this.store.selectSnapshot(DealerState.currentUser);

    // Initialize date filter options with translations
    this.dateFilterOptions = [
      { value: 'today', label: this.translationService.translate('todayText') || 'Today' },
      { value: 'yesterday', label: this.translationService.translate('yesterdayText') },
      { value: 'last7days', label: this.translationService.translate('last7DaysText') },
      { value: 'thisMonth', label: this.translationService.translate('thisMonthText') },
      { value: 'lastMonth', label: this.translationService.translate('lastMonthText') },
      { value: 'last3Months', label: this.translationService.translate('last3MonthsText') },
      { value: 'lastYear', label: this.translationService.translate('lastYearText') },
      { value: 'custom', label: this.translationService.translate('customRangeText') }
    ];

    // Set max date to today (end date cannot exceed today)
    this.maxDate = new Date();
    this.maxDate.setHours(23, 59, 59, 999);

    // Listen to dateFilter changes for audit trail
    this.auditTrailForm.get('dateFilter')?.valueChanges.subscribe((value) => {
      this.handleDateFilterChange(value, this.auditTrailForm);
    });

    this.auditTrailForm.get('barcode')?.valueChanges.subscribe(() => this.updateAuditDateValidators());

    // Any filter change requires a fresh search before export can be enabled again.
    this.auditTrailForm.valueChanges.subscribe(() => {
      this.isAuditTrailSearchCompleted = false;
    });

    // Load action list for BO Audit filter dropdown
    this.loadBoActionList();

    // Load details for audit trail filters
    void this.fetchPlants();
    void this.fetchSites();

    // plant site selection
    this.populateLocations();

    // audit trail action list
    this.loadAuditActionList();

    // fetch user login names
    this.fetchLoginNames();

    // Extract EGRA post names from EGRA_POSTS config
    this.egraPostNames = Object.keys(EGRA_POSTS);

    this.isHubLogEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.HUB_LOGS_ENABLED);
    this.isEarlyAccessEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.EARLY_ACCESS_ENABLED);
  }

  /**
   * Loads list of action types for BO Audit from the backend
   */
  private loadBoActionList(): void {
    this.actionList = buildBackofficeActionOptions((key) => this.translationService.translate(key));
  }

  loadHubProcessOptions(): void {
    if (this.isHubProcessLoading || this.isHubProcessLoaded) {
      return;
    }

    this.isHubProcessLoading = true;
    this.hubLogService
      .getHubProcessList()
      .pipe(
        timeout(15000),
        finalize(() => {
          this.isHubProcessLoading = false;
        })
      )
      .subscribe({
        next: (processes) => {
          this.hubProcess = (processes || []).filter((process) => !!process?.trim());
          this.isHubProcessLoaded = true;
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('genericErrorText') || 'Failed to load hub process list.'
          } as Alert);
        }
      });
  }

  loadTransactionTypeOptions(): void {
    if (this.isTransactionTypeLoading || this.isTransactionTypeLoaded) {
      return;
    }

    this.isTransactionTypeLoading = true;
    this.transactionLogService
      .getTransactionTypeList()
      .pipe(
        timeout(15000),
        finalize(() => {
          this.isTransactionTypeLoading = false;
        })
      )
      .subscribe({
        next: (types) => {
          this.transactionTypeList = (types || []).filter((type) => !!type?.trim());
          this.isTransactionTypeLoaded = true;
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('genericErrorText') || 'Failed to load transaction type list.'
          } as Alert);
        }
      });
  }

  /**
   * Constructs BackOfficeAuditFilter from form values and sort option
   * @param sort Sort string like 'creationDate:desc'
   * @returns BackOfficeAuditFilter object
   */
  getBoAuditFilters(sort: string): BackOfficeAuditFilter {
    const formValues = this.backOfficeAuditTrailForm.value;
    return {
      userId: formValues.userId || '',
      // The dropdown shows the friendly label but the search is performed on the action key
      action: (typeof formValues.action === 'object' ? formValues.action?.key : formValues.action) || '',
      // Backend (BackOfficeAuditTrailFilter) trims and applies dealer leading-zero padding to barcode
      barcode: formValues.barcode?.trim() || '',
      workOrderNumber: formValues.workOrderNumber?.trim() || '',
      // Force start → start of day (00:00:00) and end → end of day (23:59:59.999) so a same-day
      // search (e.g. 7/16 – 7/16) covers the full day instead of 00:00:00–00:00:00 (empty window).
      createDateStart: formValues.createDateStart ? this.toLocalDateTime(this.startOfDay(new Date(formValues.createDateStart))) : '',
      createDateEnd: formValues.createDateEnd ? this.toLocalDateTime(this.endOfDay(new Date(formValues.createDateEnd))) : '',
      sort: sort || 'creationDate:desc'
    };
  }

  /**
   * Merges a Date value and a time string like "11:00AM" or "8:00PM"
   * into a LocalDateTime string "yyyy-MM-ddTHH:mm:ss"
   */
  private combineDateAndTime(dateValue: Date | string | null, timeStr: string | null | undefined, defaultToEndOfDay = false): string {
    const trimmedTime = timeStr?.trim() ?? '';
    const hasDateValue = !!dateValue;
    const hasTimeValue = !!trimmedTime;

    // Preserve existing behavior:
    // date empty + time empty + default=false => ""
    // date empty + time empty + default=true  => current date-time
    if (!hasDateValue && !hasTimeValue) {
      return defaultToEndOfDay ? this.toLocalDateTime(new Date()) : '';
    }

    // date empty + time present => current date (start/end of day by flag)
    if (!hasDateValue && hasTimeValue) {
      const current = defaultToEndOfDay ? this.endOfDay(new Date()) : this.startOfDay(new Date());
      return this.toLocalDateTime(current);
    }

    const date = this.parseDate(dateValue);
    if (!date) {
      return '';
    }

    // date present + time empty => selected date start-of-day OR selected date end-of-day
    if (!hasTimeValue) {
      return defaultToEndOfDay ? this.toLocalDateTime(this.endOfDay(date)) : this.toLocalDateTime(this.startOfDay(date));
    }

    // date present + time present => selected date + parsed time
    this.applyParsedTime(date, trimmedTime);
    return this.toLocalDateTime(date);
  }

  private parseDate(value: Date | string): Date | null {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private endOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private applyParsedTime(date: Date, timeStr: string): void {
    const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(timeStr);
    if (!match) return;

    let hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2], 10);
    const isPm = match[3].toUpperCase() === 'PM';

    if (isPm && hours !== 12) hours += 12;
    if (!isPm && hours === 12) hours = 0;

    date.setHours(hours, minutes, 0, 0);
  }

  private toLocalDateTime(date: Date): string {
    return this.convertToLocalDateTime(date.toString());
  }

  getHubLogFilters(sort: string): HubLogFilter {
    const formValues = this.hubLogForm.value;

    let startDate = this.combineDateAndTime(formValues.date, formValues.timeFrom);

    // timeTo blank → use selected date at 23:59:59 (same day end), not today's date
    const endDateBase = formValues.date || new Date();
    let endDate = this.combineDateAndTime(endDateBase, formValues.timeTo, true);

    return {
      nativeId: formValues.process || '',
      status: formValues.status || '',
      startDate,
      endDate,
      sort: sort || 'startDate:desc'
    };
  }
  private isBOAuditTrailFilterEmpty(): boolean {
    const values = this.backOfficeAuditTrailForm.value;
    return ['userId', 'action', 'barcode', 'workOrderNumber', 'createDateStart', 'createDateEnd'].every((key) => {
      const val = values[key];
      return val === null || val === undefined || (typeof val === 'string' ? val.trim() === '' : val === '');
    });
  }

  private isHubLogFiletEmpty(): boolean {
    const values = this.hubLogForm.value;
    return ['date', 'process', 'status', 'timeFrom', 'timeTo'].every((key) => {
      const val = values[key];
      return val === null || val === undefined || (typeof val === 'string' ? val.trim() === '' : val === '');
    });
  }

  private isTransactionLogFilterEmpty(): boolean {
    const values = this.transactionLogForm.value;
    return ['userId', 'transactionType', 'transactionDate', 'transactionResult'].every((key) => {
      const val = values[key];
      return val === null || val === undefined || (typeof val === 'string' ? val.trim() === '' : val === '');
    });
  }

  /**
   * Handles date filter selection changes
   * @param filterType Selected date filter option
   * @param form The form group to update
   */
  handleDateFilterChange(filterType: string, form: FormGroup): void {
    if (filterType === 'custom') {
      this.isCustomDateRange = true;
      // Clear auto-calculated dates when switching to custom
      form.patchValue(
        {
          creationDateStart: '',
          creationDateEnd: ''
        },
        { emitEvent: false }
      );
    } else {
      this.isCustomDateRange = false;
      const dateRange = this.calculateDateRange(filterType);

      // Update form with calculated dates
      form.patchValue(
        {
          creationDateStart: dateRange.start,
          creationDateEnd: dateRange.end,
          customDateRange: { start: '', end: '' }
        },
        { emitEvent: false }
      );
    }

    this.updateAuditDateValidators();
  }

  private updateAuditDateValidators(): void {
    const dateFilterControl = this.auditTrailForm.get('dateFilter');
    const startControl = this.auditTrailForm.get('customDateRange.creationDateStart');
    const endControl = this.auditTrailForm.get('customDateRange.creationDateEnd');
    const datesRequired = !this.hasAuditBarcode;

    if (datesRequired) {
      dateFilterControl?.setValidators([Validators.required]);
      if (this.isCustomDateRange) {
        startControl?.setValidators([Validators.required]);
        endControl?.setValidators([Validators.required]);
      } else {
        startControl?.clearValidators();
        endControl?.clearValidators();
      }
    } else {
      dateFilterControl?.clearValidators();
      startControl?.clearValidators();
      endControl?.clearValidators();
    }

    dateFilterControl?.updateValueAndValidity({ emitEvent: false });
    startControl?.updateValueAndValidity({ emitEvent: false });
    endControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Fetcher function for the table component to load paginated BackOfficeAuditTrail data
   * @param filters PaginatorFilters containing page, limit, and sort info
   * @returns Observable with items and count for the table
   */
  fetcher: PaginatorFetcher<BackOfficeAuditTrail> = (filters: PaginatorFilters) => {
    if (this.isBOAuditTrailFilterEmpty()) {
      // Do not call backend, return empty result
      this.isBoAuditSearchLoading = false;
      return of({ items: [], count: 0 });
    }
    const boAuditFilters = this.getBoAuditFilters(filters.sort);
    return this.boAuditService
      .searchBoAudit(boAuditFilters, filters.page, filters.limit, Intl.DateTimeFormat().resolvedOptions().timeZone)
      .pipe(
        finalize(() => {
          this.isBoAuditSearchLoading = false;
        }),
        map((response: PaginatedBoAuditResponse) => {
          if (response?.error && response.error.length > 0) {
            console.error('BO Audit trail API Error:', response.error);
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('genericErrorText') || 'Something went wrong. Please try again.'
            } as Alert);
          } else {
            const items: BackOfficeAuditTrail[] = response.data.boAuditTrail || [];
            this.isBoAuditSearchCompleted = true;
            const count = this.getPaginatedCount(response.data.pagination, filters.limit, items.length);
            return { items, count };
          }
        }),
        catchError((error) => {
          console.error('BO Audit trail API Error:', error);
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('genericErrorText') || 'Something went wrong. Please try again.'
          } as Alert);
          return of({ items: [], count: 0 });
        })
      );
  };

  /**
   * Converts a JS Date string (e.g. 'Tue Jul 01 2025 00:00:00 GMT+0000') to 'yyyy-MM-ddTHH:mm:ss' format for Java LocalDateTime
   */
  private convertToLocalDateTime(dateStr: string): string {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => (n < 10 ? '0' + n : n);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}:${pad(date.getSeconds())}`;
  }

  private getPaginatedCount(
    pagination: { totalRecords: number; currentPage: number; nextPage: number; previousPage: number },
    pageSize: number,
    fallbackCount: number
  ): number {
    if (pagination.totalRecords !== -1) {
      return pagination.totalRecords;
    }
    if (pagination.nextPage) {
      return (pagination.currentPage + 1) * pageSize;
    }
    if (pagination.previousPage) {
      return pagination.currentPage * pageSize;
    }
    return fallbackCount;
  }

  /**
   * Populates locations array by combining plantNames and siteNames
   */
  populateLocations(): void {
    // Dedupe: mic-dropdown-search tracks options by value, duplicates trigger NG0955
    this.locations = Array.from(new Set([...this.plantNames, ...this.siteNames]));
  }

  /**
   * Fetches login names for the current dealer from the backend
   */
  fetchLoginNames(): void {
    this.auditService.getLoginNames(this.dealer.id).subscribe({
      next: (response) => {
        this.userLoginNames = response?.data || [];
      },
      error: () => {
        this.userLoginNames = [];
      }
    });
  }

  fetchPlants(): Promise<void> {
    return new Promise((resolve) => {
      const plants = this.store.selectSnapshot(DealerState.plants);
      if (plants && plants.length > 0) {
        this.plantNames = this.formatLocationNames(plants);
        resolve();
      } else {
        this.plantService.getPlants(this.dealer.id).subscribe((plants) => {
          this.store.dispatch(new UpdatePlantsAction(plants));
          this.plantNames = this.formatLocationNames(plants);
          resolve();
        });
      }
    });
  }

  fetchSites(): Promise<void> {
    return new Promise((resolve) => {
      const sites = this.store.selectSnapshot(DealerState.sites);
      if (sites && sites.length > 0) {
        this.siteNames = this.formatLocationNames(sites);
        resolve();
      } else {
        this.siteService.getSites(this.dealer.id).subscribe((sites) => {
          this.store.dispatch(new UpdateSitesAction(sites));
          this.siteNames = this.formatLocationNames(sites);
          resolve();
        });
      }
    });
  }

  private formatLocationNames(locations: Array<{ code: string; name: string }>): string[] {
    return Array.from(new Set(locations.map(({ code, name }) => `${code}-${name}`)));
  }

  /**
   * Constructs BackOfficeAuditFilter from form values and sort option
   * @param sort Sort string like 'creationDate:desc'
   * @returns BackOfficeAuditFilter object
   */
  getAuditFilters(sort: string): AuditFilter {
    const formValues = this.auditTrailForm.value;

    // Get dates from either auto-calculated or custom range
    let startDate = '';
    let endDate = '';

    if (this.isCustomDateRange && formValues.customDateRange) {
      // Custom range: convert Date objects to LocalDateTime string
      startDate = formValues.customDateRange.creationDateStart
        ? this.convertToLocalDateTime(formValues.customDateRange.creationDateStart)
        : '';
      // End date must cover the entire day, so force the time component to 23:59:59
      endDate = formValues.customDateRange.creationDateEnd
        ? this.toLocalDateTime(this.endOfDay(new Date(formValues.customDateRange.creationDateEnd)))
        : '';
    } else if (formValues.dateFilter && formValues.dateFilter !== 'custom') {
      // Predefined filters: calculateDateRange already returns formatted strings
      const dateRange = this.calculateDateRange(formValues.dateFilter);
      startDate = dateRange.start;
      endDate = dateRange.end;
    }

    return {
      userId: formValues.userId || '',
      barcode: formValues.barcode || '',
      code: formValues.location ? formValues.location.split('-')[0].trim() : '',
      creationDateStart: startDate,
      creationDateEnd: endDate,
      postName: formValues.postName || '',
      action: formValues.auditAction || '',
      sort: sort || 'creationDate:desc'
    };
  }

  get hasAuditBarcode(): boolean {
    const barcode = this.auditTrailForm.get('barcode')?.value;
    return typeof barcode === 'string' && barcode.trim().length > 0;
  }

  /**
   * Checks if all filter fields in auditTrailForm are empty
   * @returns boolean
   */
  private isAuditTrailFilterEmpty(): boolean {
    const values = this.auditTrailForm.value;

    if (this.hasAuditBarcode) {
      return false;
    }

    // Without a barcode, dateFilter is mandatory.
    if (!values.dateFilter) {
      return true;
    }

    // If dateFilter is 'custom', check if custom dates are provided
    if (this.isCustomDateRange && values.customDateRange) {
      if (!values.customDateRange.creationDateStart || !values.customDateRange.creationDateEnd) {
        return true; // Custom range selected but dates not provided
      }
    }

    return false; // dateFilter is selected and valid
  }

  /**
   * Fetcher function for the table component to load paginated AuditTrail data
   * @param filters PaginatorFilters containing page, limit, and sort info
   * @returns Observable with items and count for the table
   */
  fetcherAudit: PaginatorFetcher<AuditTrail> = (filters: PaginatorFilters) => {
    if (this.isAuditTrailFilterEmpty()) {
      // Do not call backend, return empty result
      this.isAuditTrailSearchLoading = false;
      this.isAuditTrailSearchCompleted = false;
      return of({ items: [], count: 0 });
    }
    const auditFilters = this.getAuditFilters(filters.sort);
    return this.auditService.searchAudit(auditFilters, filters.page, filters.limit, Intl.DateTimeFormat().resolvedOptions().timeZone).pipe(
      finalize(() => {
        this.isAuditTrailSearchLoading = false;
      }),
      map((response: PaginatedAuditResponse) => {
        if (response?.error && response.error.length > 0) {
          console.error('Audit trail API Error:', response.error);
          this.isAuditTrailSearchCompleted = false;
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('genericErrorText') || 'Something went wrong. Please try again.'
          } as Alert);
        } else {
          const items: AuditTrail[] = response.data.auditTrail || [];
          this.isAuditTrailSearchCompleted = true;
          if (this.isEarlyAccessEnabled) {
            this.loadAuditExportLimit();
          }
          const count = this.getPaginatedCount(response.data.pagination, filters.limit, items.length);
          return { items, count };
        }
      }),
      catchError((error) => {
        console.error('Audit trail API Error:', error);
        this.isAuditTrailSearchCompleted = false;
        const errorMessage = this.getAuditTrailErrorMessage(error);
        this.alertService.showAlert({
          type: AlertType.critical,
          content: errorMessage
        } as Alert);
        return of({ items: [], count: 0 });
      })
    );
  };

  /**
   * Feature flag to enable/disable Back Office Audit functionality
   */
  tableConfigAudit: TableConfig<AuditTrail> = {
    columns: {
      userId: {
        cell: (item: AuditTrail) => item.userId,
        header: this.translationService.translate('userIdText')
      },
      record: {
        cell: (item: AuditTrail) => item.barcode,
        header: this.translationService.translate('barcodeText')
      },
      btm: {
        cell: (item: AuditTrail) => item.location,
        header: this.translationService.translate('plantSiteText')
      },
      date: {
        cell: (item: AuditTrail) => item.creationDate,
        header: this.translationService.translate('dateText'),
        sortable: true
      },
      table: {
        cell: (item: AuditTrail) => item.postName,
        header: this.translationService.translate('postText')
      },
      action: {
        cell: (item: AuditTrail) => item.casingEditInfo,
        header: this.translationService.translate('actionText')
      }
    },
    paginator: {
      pageSize: 10,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: 'Items per page'
      }
    }
  };

  /**
   * Loads list of action types for BO Audit from the backend
   */
  private loadAuditActionList(): void {
    this.auditActionList = ActionState;
  }

  clearSelection(): void {
    this.auditTrailForm.reset();
    this.isCustomDateRange = false;
    this.isAuditTrailSearchCompleted = false;
  }

  clearBoSelection(): void {
    this.backOfficeAuditTrailForm.reset();
    this.isBoAuditSearchCompleted = false;
  }

  activateBoAuditTable() {
    this.boAuditTableActive = true;
  }

  activeHubLogTable() {
    this.hubLogTableActive = true;
  }

  activateTransactionLogTable(): void {
    this.transactionLogTableActive = true;
  }

  onTabChange(event: any) {
    // Tab index 1 is the Back Office Audit Trail tab
    if (event.index === 1) {
      this.activateBoAuditTable();
    }
    if (event.index === 2) {
      this.activateTransactionLogTable();
      // Force table refresh after render to ensure data loads
      setTimeout(() => {
        if (this.tableRefTransactionLog) {
          this.tableRefTransactionLog.patchExtraFilters(this.transactionLogForm.value, true);
        }
      });
    }
    if (event.index === 3) {
      this.activeHubLogTable();
    }
  }

  private patchTimepickerDecrement(): void {
    this.timepickers?.forEach((tp: any) => {
      tp.decrementHour = () => {
        if (!tp.hour.value) return;
        const current = +tp.hour.value;
        if (current > 0) {
          tp.hour.setValue((current - 1).toString());
          tp.addLeadingZero();
          tp.validateHour(tp.hour.value);
        }
      };
    });
  }
  /**
   * Subscribes to hour/minutes value changes on each timepicker and
   * automatically sets the period (AM/PM) to 'AM' when the user modifies
   * the time via arrow keys and no period has been selected yet.
   */
  private patchTimepickerDefaultPeriod(): void {
    this.timepickers?.forEach((tp: any) => {
      const setDefaultPeriod = () => {
        if (!tp.period.value) {
          tp.period.setValue('AM');
          tp.onChange();
        }
      };
      tp.hour?.valueChanges.subscribe((val: string) => {
        if (val && val.toString().trim() !== '') {
          setDefaultPeriod();
        }
      });
      tp.minutes?.valueChanges.subscribe((val: string) => {
        if (val && val.toString().trim() !== '') {
          setDefaultPeriod();
        }
      });
    });
  }

  clearHubSelection(): void {
    this.hubLogForm.reset();
    // Clear internal timepicker hour/minutes controls
    this.timepickers?.forEach((tp: any) => {
      tp.hour?.setValue('');
      tp.minutes?.setValue('');
      tp.period?.setValue('');
    });
  }

  onTimePickerInteract(controlName: string): void {
    const control = this.hubLogForm.get(controlName);
    if (!control?.value || control.value.trim() === '') {
      control.setValue('00:00');
    }
  }

  closeHubLogDetails(): void {
    this.showHubLogDetails = false;
    this.selectedHubLogForDetails = null;
  }

  clearHubLogs(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const dialogRef = this.dialog.open(SharedDialogComponent, {
      width: '550px',
      data: {
        title: this.translationService.translate('clearLogsText'),
        message:
          this.translationService.translate('deleteCamSpecModalContentLine1Text') +
          ' ' +
          this.translationService.translate('hubEventLogText') +
          '? ' +
          this.translationService.translate('deleteCamSpecModalContentLine2Text'),
        buttonText: this.translationService.translate('clearLogsText'),
        cancelButtonText: this.translationService.translate('cancelText')
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.hubLogService.clearHubLogs().subscribe({
          next: (response: any) => {
            if (response?.error && response.error.length > 0) {
              this.showHubLogDeleteError();
              return;
            }

            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('deleteSuccessText') || 'Hub record deleted successfully.'
            } as Alert);

            this.showHubLogDetails = false;
            this.selectedHubLogForDetails = null;
            this.deletedHubNativeIds.clear();
            this.tableRef3?.patchExtraFilters({ ...this.hubLogForm.value, _refreshToken: Date.now() }, true);
          },
          error: () => {
            this.showHubLogDeleteError();
          }
        });
      }
    });
  }

  private showHubLogDeleteError(fallbackMessage = 'Failed to delete hub record.'): void {
    this.alertService.showAlert({
      type: AlertType.critical,
      content: this.translationService.translate('genericErrorText') || fallbackMessage
    } as Alert);
  }

  showHubLogDetailsTable(item: HubLog): void {
    this.selectedHubLogForDetails = item;

    const refreshFilters = {
      nativeId: item?.nativeId,
      refreshToken: Date.now()
    };

    if (!this.showHubLogDetails) {
      this.showHubLogDetails = true;
      // Wait until the details table is rendered, then force fetch.
      setTimeout(() => {
        this.tableRefHubEvent?.patchExtraFilters(refreshFilters, true);
      });
      return;
    }

    // Already visible: force re-fetch for subsequent clicks.
    this.tableRefHubEvent?.patchExtraFilters(refreshFilters, true);
  }

  /**
   * Triggers audit table fetch when search button is clicked
   */
  submitSearch(): void {
    if (!this.validateAuditTrailSearchInputs()) {
      return;
    }

    this.isAuditTrailSearchLoading = true;
    if (this.tableRef2) {
      this.tableRef2.patchExtraFilters({ ...this.auditTrailForm.value, _refreshToken: Date.now() }, true);
    }
  }

  openAuditExportModal(): void {
    if (!this.canOpenAuditExport()) {
      return;
    }

    if (this.dialog.getDialogById(this.auditExportModalId)) {
      return;
    }

    const data: ExportModalData = {
      columns: this.getAuditExportColumns(),
      fileNamePrefix: 'AuditTrail',
      maxExportRows: this.auditExportMaxRows,
      // Providing dataFetcher (instead of excelExportCallback) makes the modal render the
      // shared <app-export-to-excel> component, which owns the fetch → XLS pipeline.
      dataFetcher: this.auditExportDataFetcher
    };

    this.dialog.open(ExportModalComponent, {
      id: this.auditExportModalId,
      data,
      width: '480px'
    });
  }

  private getAuditExportColumns(): ExportColumnDef[] {
    return [
      { key: 'userId', label: this.translationService.translate('userIdText'), selected: true },
      { key: 'barcode', label: this.translationService.translate('barcodeText'), selected: true },
      { key: 'location', label: this.translationService.translate('plantSiteText'), selected: true },
      { key: 'creationDate', label: this.translationService.translate('dateText'), selected: true },
      { key: 'postName', label: this.translationService.translate('postText'), selected: true },
      { key: 'casingEditInfo', label: this.translationService.translate('actionText'), selected: true }
    ];
  }

  /**
   * Fetcher handed to the shared <app-export-to-excel> component (via ExportModalComponent).
   * It only resolves the rows; column mapping, file naming and XLS writing stay in the shared
   * component/service so audit export behaves exactly like the other screens.
   */
  auditExportDataFetcher = (): Observable<ExportResponse> => {
    this.isAuditTrailExportLoading = true;
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const auditFilters = this.getAuditFilters('creationDate:desc');

    return this.fetchAuditExportRows(auditFilters, localTimeZone).pipe(
      map((rows) => ({ data: rows as Record<string, any>[] } as ExportResponse)),
      catchError((error) => {
        this.isAuditTrailExportLoading = false;
        throw error;
      }),
      finalize(() => {
        this.isAuditTrailExportLoading = false;
      })
    );
  };

  private fetchAuditExportRows(filters: AuditFilter, localTimeZone: string): Observable<AuditTrail[]> {
    if (this.auditExportMaxRows > this.auditExportChunkSize) {
      return this.fetchAuditExportPage(filters, localTimeZone, 1, []);
    }

    const ranges = this.getAuditExportDateRanges(filters);
    return this.fetchAuditExportRange(ranges, filters, localTimeZone, 0, []);
  }

  private fetchAuditExportPage(
    filters: AuditFilter,
    localTimeZone: string,
    pageNumber: number,
    accumulatedRows: AuditTrail[]
  ): Observable<AuditTrail[]> {
    if (accumulatedRows.length >= this.auditExportMaxRows) {
      return of(accumulatedRows.slice(0, this.auditExportMaxRows));
    }

    const pageSize = Math.min(this.auditExportChunkSize, this.auditExportMaxRows - accumulatedRows.length);
    return this.auditService.searchAudit(filters, pageNumber, pageSize, localTimeZone).pipe(
      map((response: PaginatedAuditResponse) => {
        if (response?.error && response.error.length > 0) {
          throw response.error;
        }

        const pageRows: AuditTrail[] = response?.data?.auditTrail || [];
        return [...accumulatedRows, ...pageRows.slice(0, pageSize)];
      }),
      concatMap((rows) => {
        const pageRowCount = rows.length - accumulatedRows.length;
        return rows.length >= this.auditExportMaxRows || pageRowCount < this.auditExportChunkSize
          ? of(rows)
          : this.fetchAuditExportPage(filters, localTimeZone, pageNumber + 1, rows);
      })
    );
  }

  private fetchAuditExportRange(
    ranges: Array<Pick<AuditFilter, 'creationDateStart' | 'creationDateEnd'>>,
    filters: AuditFilter,
    localTimeZone: string,
    rangeIndex: number,
    accumulatedRows: AuditTrail[]
  ): Observable<AuditTrail[]> {
    if (rangeIndex >= ranges.length || accumulatedRows.length >= this.auditExportMaxRows) {
      return of(accumulatedRows.slice(0, this.auditExportMaxRows));
    }

    const remainingRows = this.auditExportMaxRows - accumulatedRows.length;
    const rangeFilters = { ...filters, ...ranges[rangeIndex] };
    return this.auditService.searchAuditExport(rangeFilters, localTimeZone).pipe(
      map((response: AuditExportResponse) => {
        if (response?.error && response.error.length > 0) {
          throw response.error;
        }

        const rows: AuditTrail[] = response?.data?.auditTrail || [];
        return [...accumulatedRows, ...rows.slice(0, remainingRows)];
      }),
      concatMap((rows) =>
        rows.length >= this.auditExportMaxRows || rangeIndex + 1 >= ranges.length
          ? of(rows)
          : this.fetchAuditExportRange(ranges, filters, localTimeZone, rangeIndex + 1, rows)
      )
    );
  }

  private getAuditExportDateRanges(filters: AuditFilter): Array<Pick<AuditFilter, 'creationDateStart' | 'creationDateEnd'>> {
    const originalRange = { creationDateStart: filters.creationDateStart, creationDateEnd: filters.creationDateEnd };
    const selectedDateFilter = this.auditTrailForm.get('dateFilter')?.value;
    const shouldSplit = selectedDateFilter === 'lastYear' || this.isCustomAuditRangeOverThreeMonths(filters);

    if (!shouldSplit) {
      return [originalRange];
    }

    const rangeStart = this.parseDate(filters.creationDateStart);
    const rangeEnd = this.parseDate(filters.creationDateEnd);
    if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) {
      return [originalRange];
    }

    const ranges: Array<Pick<AuditFilter, 'creationDateStart' | 'creationDateEnd'>> = [];
    let chunkStart = this.startOfDay(rangeStart);
    const endOfRange = this.endOfDay(rangeEnd);

    while (chunkStart <= endOfRange) {
      const nextChunkStart = this.addCalendarMonths(chunkStart, 3);
      const chunkEnd = nextChunkStart < endOfRange ? new Date(nextChunkStart.getTime() - 1000) : endOfRange;
      ranges.push({
        creationDateStart: this.convertToLocalDateTime(chunkStart.toString()),
        creationDateEnd: this.convertToLocalDateTime(chunkEnd.toString())
      });
      chunkStart = nextChunkStart;
    }

    return ranges.reverse();
  }

  private isCustomAuditRangeOverThreeMonths(filters: AuditFilter): boolean {
    if (this.auditTrailForm.get('dateFilter')?.value !== 'custom') {
      return false;
    }

    const rangeStart = this.parseDate(filters.creationDateStart);
    const rangeEnd = this.parseDate(filters.creationDateEnd);
    if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) {
      return false;
    }

    const threeMonthBoundary = this.addCalendarMonths(this.startOfDay(rangeStart), 3);
    return this.startOfDay(rangeEnd) > threeMonthBoundary;
  }

  private addCalendarMonths(date: Date, months: number): Date {
    const result = new Date(date);
    const originalDay = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + months);
    const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(originalDay, lastDayOfTargetMonth));
    return result;
  }

  private loadAuditExportLimit(): void {
    if (this.isAuditExportLimitLoading) {
      return;
    }

    this.isAuditExportLimitLoading = true;
    this.auditExportMaxRows = this.defaultAuditExportMaxRows;
    this.auditService
      .getAuditExportLimit(this.dealer?.id)
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this.isAuditExportLimitLoading = false;
        })
      )
      .subscribe((response) => {
        if (response?.error?.length) {
          return;
        }

        const limit = response?.data;
        if (typeof limit === 'number' && Number.isFinite(limit)) {
          this.auditExportMaxRows = limit;
        } else if (typeof limit === 'string' && limit.trim() !== '' && Number.isFinite(Number(limit))) {
          this.auditExportMaxRows = Number(limit);
        }
      });
  }

  private getBoAuditExportColumns(): ExportColumnDef[] {
    return [
      { key: 'userId', label: this.translationService.translate('userIdText'), selected: true },
      { key: 'btm', label: this.translationService.translate('btmWorkOrderText'), selected: true },
      { key: 'createDt', label: this.translationService.translate('dateText'), selected: true },
      { key: 'table', label: this.translationService.translate('tableText'), selected: true },
      { key: 'action', label: this.translationService.translate('actionText'), selected: true },
      { key: 'keyData', label: this.translationService.translate('recordText'), selected: true },
      { key: 'details', label: this.translationService.translate('detailsText'), selected: true }
    ];
  }

  boAuditExportDataFetcher = (): Observable<ExportResponse> => {
    this.isBoAuditExportLoading = true;
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const boAuditFilters = this.getBoAuditFilters('creationDate:desc');

    return this.boAuditService.searchBoAuditExport(boAuditFilters, localTimeZone).pipe(
      map((response: BoAuditExportResponse) => {
        if (response?.error && response.error.length > 0) {
          throw response.error;
        }

        const rows: BackOfficeAuditTrail[] = response?.data?.boAuditTrail || [];
        return { data: rows as Record<string, any>[] } as ExportResponse;
      }),
      finalize(() => {
        this.isBoAuditExportLoading = false;
      })
    );
  };

  openBoAuditExportModal(): void {
    if (!this.canOpenBoAuditExport() || this.dialog.getDialogById(this.boAuditExportModalId)) {
      return;
    }

    const data: ExportModalData = {
      columns: this.getBoAuditExportColumns(),
      fileNamePrefix: 'BackOfficeAuditTrail',
      maxExportRows: this.boAuditExportMaxRows,
      dataFetcher: this.boAuditExportDataFetcher
    };

    this.dialog.open(ExportModalComponent, {
      id: this.boAuditExportModalId,
      data,
      width: '480px'
    });
  }

  canOpenBoAuditExport(): boolean {
    return (
      this.isEarlyAccessEnabled &&
      !this.isBOAuditTrailFilterEmpty() &&
      this.isBoAuditSearchCompleted &&
      !this.isBoAuditSearchLoading &&
      !this.isBoAuditExportLoading
    );
  }

  private validateAuditTrailSearchInputs(): boolean {
    this.validateDateRange();
    const dateFilter = this.auditTrailForm.get('dateFilter')?.value;

    if (!dateFilter && !this.hasAuditBarcode) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('dateFilterRequiredText') || 'Please select a date filter to search.'
      } as Alert);
      return false;
    }

    if (!this.isCustomDateRange) {
      return true;
    }

    const customRange = this.auditTrailForm.get('customDateRange');
    const startControl = customRange?.get('creationDateStart');
    const endControl = customRange?.get('creationDateEnd');
    const startDate = startControl?.value;
    const endDate = endControl?.value;
    const customDateRangeRequiredText = this.translationService.translate('customDateRangeRequiredText');

    if ((!startDate || !endDate) && !this.hasAuditBarcode) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: customDateRangeRequiredText || 'Please select both start and end dates for custom range.'
      } as Alert);
      return false;
    }

    if (startControl?.hasError('dateRangeExceeded') || endControl?.hasError('dateRangeExceeded')) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content:
          this.translationService.translate('dateRangeExceededText') ||
          'Date range must not exceed 365 days and must be within the last 3 years.'
      } as Alert);
      return false;
    }

    return true;
  }

  canOpenAuditExport(): boolean {
    return (
      this.isEarlyAccessEnabled &&
      this.hasValidAuditFilterSelection() &&
      this.isAuditTrailSearchCompleted &&
      !this.isAuditTrailSearchLoading &&
      !this.isAuditExportLimitLoading &&
      !this.isAuditTrailExportLoading
    );
  }

  private hasValidAuditFilterSelection(): boolean {
    const dateFilter = this.auditTrailForm.get('dateFilter')?.value;
    if (!dateFilter && !this.hasAuditBarcode) {
      return false;
    }

    if (!this.isCustomDateRange) {
      return true;
    }

    const customRange = this.auditTrailForm.get('customDateRange');
    const startDate = customRange?.get('creationDateStart')?.value;
    const endDate = customRange?.get('creationDateEnd')?.value;
    const startControl = customRange?.get('creationDateStart');
    const endControl = customRange?.get('creationDateEnd');

    return (
      ((!!startDate && !!endDate) || this.hasAuditBarcode) &&
      !startControl?.hasError('dateRangeExceeded') &&
      !endControl?.hasError('dateRangeExceeded')
    );
  }

  submitSearchBO(): void {
    this.isBoAuditSearchCompleted = false;
    this.isBoAuditSearchLoading = true;
    this.tableRef.patchExtraFilters({ ...this.backOfficeAuditTrailForm.value, _refreshToken: Date.now() }, true);
  }

  getTransactionLogFilters(sort: string): TransactionLogFilter {
    const formValues = this.transactionLogForm.value;
    // Convert "Failed" display value to "Fail" for API call
    const transactionResult = formValues.transactionResult === 'Failed' ? 'Fail' : formValues.transactionResult || '';
    return {
      userId: formValues.userId || '',
      transactionType: formValues.transactionType || '',
      transactionDate: formValues.transactionDate ? this.convertToLocalDateTime(formValues.transactionDate.toString()) : '',
      transactionResult: transactionResult,
      sort: sort || 'transactionDate:desc'
    };
  }

  fetcherTransactionLog: PaginatorFetcher<TransactionLog> = (filters: PaginatorFilters) => {
    if (this.isTransactionLogFilterEmpty()) {
      this.isTransactionLogSearchLoading = false;
      return of({ items: [], count: 0 });
    }
    const txnFilters = this.getTransactionLogFilters(filters.sort);
    return this.transactionLogService
      .searchTransactionLog(txnFilters, filters.page, filters.limit, Intl.DateTimeFormat().resolvedOptions().timeZone)
      .pipe(
        finalize(() => {
          this.isTransactionLogSearchLoading = false;
        }),
        map((response: PaginatedTransactionLogResponse) => {
          if (response?.error && response.error.length > 0) {
            console.error('Transaction Log API Error:', response.error);
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('genericErrorText') || 'Something went wrong. Please try again.'
            } as Alert);
          } else {
            const items: TransactionLog[] = response.data.transactionLogs || [];
            const count = this.getPaginatedCount(response.data.pagination, filters.limit, items.length);
            return { items, count };
          }
        }),
        catchError((error) => {
          console.error('Transaction Log API Error:', error);
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('genericErrorText') || 'Something went wrong. Please try again.'
          } as Alert);
          return of({ items: [], count: 0 });
        })
      );
  };

  clearTransactionLogSelection(): void {
    this.transactionLogForm.reset({ transactionResult: '' });
  }

  submitSearchTransactionLog(): void {
    this.isTransactionLogSearchLoading = true;
    if (!this.transactionLogTableActive) {
      this.transactionLogTableActive = true;
      // Wait for table to render before patching filters
      setTimeout(() => {
        if (this.tableRefTransactionLog) {
          this.tableRefTransactionLog.patchExtraFilters({ ...this.transactionLogForm.value, _refreshToken: Date.now() }, true);
        }
      });
      return;
    }
    if (this.tableRefTransactionLog) {
      this.tableRefTransactionLog.patchExtraFilters({ ...this.transactionLogForm.value, _refreshToken: Date.now() }, true);
    }
  }

  /** Returns true if the value is blank (ignored) or matches H:mm AM/PM */
  private isValidTimeString(timeStr: string | null | undefined): boolean {
    if (!timeStr?.trim()) return true;
    return /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.test(timeStr.trim());
  }

  submitSearchHub(): void {
    const timeFrom = this.hubLogForm.get('timeFrom')?.value;
    const timeTo = this.hubLogForm.get('timeTo')?.value;

    if (!this.isValidTimeString(timeFrom)) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('invalidTimeText') || 'Timestamp is invalid.'
      } as Alert);
      return;
    }

    if (!this.isValidTimeString(timeTo)) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('invalidTimeText') || 'Timestamp is invalid.'
      } as Alert);
      return;
    }

    this.showHubLogDetails = false;
    this.selectedHubLogForDetails = null;
    this.deletedHubNativeIds.clear();

    this.isHubLogSearchLoading = true;
    if (this.tableRef3) {
      this.tableRef3.patchExtraFilters({ ...this.hubLogForm.value, _refreshToken: Date.now() }, true);
    }
  }

  /**
   * Calculates start and end dates based on selected filter option
   * @param filterType The selected date filter option
   * @returns Object with start and end date strings
   */
  calculateDateRange(filterType: string): { start: string; end: string } {
    const today = new Date();
    const startDate = new Date();
    const endDate = new Date();

    switch (filterType) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'yesterday':
        startDate.setDate(today.getDate() - 1);
        endDate.setDate(today.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'last7days':
        startDate.setDate(today.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'thisMonth':
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'lastMonth':
        startDate.setMonth(today.getMonth() - 1);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(today.getMonth());
        endDate.setDate(0); // Last day of previous month
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'last3Months':
        startDate.setMonth(today.getMonth() - 3);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'lastYear':
        startDate.setFullYear(today.getFullYear() - 1);
        startDate.setMonth(0); // January
        startDate.setDate(1); // 1st day
        startDate.setHours(0, 0, 0, 0);

        endDate.setFullYear(today.getFullYear() - 1);
        endDate.setMonth(11); // December
        endDate.setDate(31); // 31st day
        endDate.setHours(23, 59, 59, 999);
        break;

      default:
        return { start: '', end: '' };
    }

    return {
      start: this.convertToLocalDateTime(startDate.toString()),
      end: this.convertToLocalDateTime(endDate.toString())
    };
  }

  onCustomDateRangeChange(): void {
    this.validateDateRange();
    const customRange = this.auditTrailForm.get('customDateRange');
    const startValue = customRange?.get('creationDateStart')?.value;
    const endValue = customRange?.get('creationDateEnd')?.value;

    if (startValue && endValue) {
      console.log('Custom Start date selected:', this.convertToLocalDateTime(startValue));
      console.log('Custom End date selected:', this.convertToLocalDateTime(endValue));
    }
  }

  onDateFilterChange(event: MatSelectChange): void {
    const customDateRangeGroup = this.auditTrailForm.get('customDateRange');

    if (event.value === 'custom') {
      this.isCustomDateRange = true;

      // Log custom dates if already selected
      const startDate = customDateRangeGroup?.get('creationDateStart')?.value;
      const endDate = customDateRangeGroup?.get('creationDateEnd')?.value;

      if (startDate && endDate) {
        console.log('Custom Start date:', this.convertToLocalDateTime(startDate));
        console.log('Custom End date:', this.convertToLocalDateTime(endDate));
      }
    } else {
      this.isCustomDateRange = false;

      // Calculate and log dates for predefined filters
      const dateRange = this.calculateDateRange(event.value);
      console.log('Selected date filter:', event.value);
      console.log('Start date:', dateRange.start);
      console.log('End date:', dateRange.end);
    }

    this.updateAuditDateValidators();
  }

  onStartDateChange(event: any): void {
    const startDate = event.value;
    if (startDate) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      const threeYearsAgo = new Date();
      threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
      threeYearsAgo.setHours(0, 0, 0, 0);

      // Validate that start date is within allowed range (last 3 years)
      if (startDate < threeYearsAgo || startDate > today) {
        const customDateRange = this.auditTrailForm.get('customDateRange');
        customDateRange?.setErrors({ dateRangeExceeded: true });
        return;
      }
    }

    this.validateDateRange();
    this.onCustomDateRangeChange();
  }

  onEndDateChange(event: any): void {
    const endDate = event.value;
    if (endDate) {
      // Validate the range
      this.validateDateRange();
      this.onCustomDateRangeChange();
    }
  }

  validateDateRange(): void {
    const customDateRange = this.auditTrailForm.get('customDateRange');
    const startControl = customDateRange?.get('creationDateStart');
    const endControl = customDateRange?.get('creationDateEnd');
    const startDate = startControl?.value;
    const endDate = endControl?.value;

    if (startDate && endDate) {
      const daysDifference = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      const threeYearsAgo = new Date();
      threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
      threeYearsAgo.setHours(0, 0, 0, 0);

      if (daysDifference > 365 || endDate > today || startDate < threeYearsAgo) {
        // Set error on individual controls, not the group
        const startErrors = startControl?.errors || {};
        const endErrors = endControl?.errors || {};

        startControl?.setErrors({ ...startErrors, dateRangeExceeded: true });
        endControl?.setErrors({ ...endErrors, dateRangeExceeded: true });

        // Mark as touched to trigger mat-error display
        startControl?.markAsTouched();
        endControl?.markAsTouched();
      } else {
        // Clear only dateRangeExceeded error
        const startErrors = { ...startControl?.errors };
        const endErrors = { ...endControl?.errors };

        delete startErrors['dateRangeExceeded'];
        delete endErrors['dateRangeExceeded'];

        startControl?.setErrors(Object.keys(startErrors).length ? startErrors : null);
        endControl?.setErrors(Object.keys(endErrors).length ? endErrors : null);
      }
    }
  }

  /**
   * Fetcher function for the table component to load paginated BackOfficeAuditTrail data
   * @param filters PaginatorFilters containing page, limit, and sort info
   * @returns Observable with items and count for the table
   */
  fetcherHub: PaginatorFetcher<HubLog> = (filters: PaginatorFilters) => {
    if (this.isHubLogFiletEmpty()) {
      this.isHubLogSearchLoading = false;
      return of({ items: [], count: 0 });
    }
    const hubFilters = this.getHubLogFilters(filters.sort);
    return this.hubLogService.searchHubLog(hubFilters, filters.page, filters.limit).pipe(
      finalize(() => {
        this.isHubLogSearchLoading = false;
      }),
      map((response: PaginatedHubLogResponse) => {
        if (response?.error && response.error.length > 0) {
          console.error('Hub Log API Error:', response.error);
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('genericErrorText') || 'Something went wrong. Please try again.'
          } as Alert);
        } else {
          const serverItems: HubLog[] = response.data.hubJobs || [];
          const items = serverItems.filter((it) => !this.deletedHubNativeIds.has(it.nativeId));
          let count = this.getPaginatedCount(response.data.pagination, filters.limit, items.length);
          if (response.data.pagination.totalRecords !== -1) {
            const removedOnPage = serverItems.length - items.length;
            count = Math.max(0, count - removedOnPage);
          }
          return { items, count };
        }
      }),
      catchError((error) => {
        console.error('Hub Log API Error:', error);
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('genericErrorText') || 'Something went wrong. Please try again.'
        } as Alert);
        return of({ items: [], count: 0 });
      })
    );
  };

  fetcherHubEvent: PaginatorFetcher<HubEventLog> = (filters: PaginatorFilters) => {
    const nativeId = (filters.extraFilters as { nativeId?: string } | undefined)?.nativeId || this.selectedHubLogForDetails?.nativeId;

    if (!nativeId) {
      return of({ items: [], count: 0 });
    }

    return this.hubLogService.searchHubEventLog(nativeId, filters.page, filters.limit).pipe(
      map((response: PaginatedHubEventLogResponse) => {
        if (response?.error && response.error.length > 0) {
          console.error('Hub Event Log API Error:', response.error);
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('genericErrorText') || 'Something went wrong. Please try again.'
          } as Alert);
        } else {
          const items: HubEventLog[] = response.data.hubEventLog || [];
          const count = this.getPaginatedCount(response.data.pagination, filters.limit, items.length);
          return { items, count };
        }
      })
    );
  };

  private readonly deletedHubNativeIds = new Set<string>();

  removeHubRecord(item: HubLog): void {
    const nativeId = item?.nativeId;
    if (!nativeId) {
      this.showHubLogDeleteError('Unable to delete record.');
      return;
    }

    this.isDeleteHubRecordLoading = true;
    this.hubLogService
      .deleteHubLog(nativeId)
      .pipe(
        finalize(() => {
          this.isDeleteHubRecordLoading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response?.error && response.error.length > 0) {
            this.showHubLogDeleteError();
            return;
          }

          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('deleteSuccessText') || 'Hub record deleted successfully.'
          } as Alert);

          if (this.selectedHubLogForDetails?.nativeId === nativeId) {
            this.showHubLogDetails = false;
            this.selectedHubLogForDetails = null;
          }

          this.deletedHubNativeIds.add(nativeId);
          if (this.tableRef3) {
            this.tableRef3.patchExtraFilters({ ...this.hubLogForm.value, _refreshToken: Date.now() }, true);
          }
        },
        error: () => {
          this.showHubLogDeleteError();
        }
      });
  }

  /**
   * Handles error response from Audit Trail search API
   * Checks for BARCODE_NOT_FOUND error code and displays translated barcodeNotFoundText
   * Defaults to genericErrorText for other errors or internal server errors
   * @param error The HTTP error response
   * @returns The error message to display
   */
  private getAuditTrailErrorMessage(error: any): string {
    // Check if errorCode is BARCODE_NOT_FOUND
    if (error?.error?.errorCode === 'BARCODE_NOT_FOUND') {
      return this.translationService.translate('barcodeNotFoundText') || error.error.message;
    }

    // Default to generic error text for all other errors
    return this.translationService.translate('genericErrorText') || 'Something went wrong. Please try again.';
  }
}
